<script setup lang="ts">
import {computed} from 'vue'
import {useI18n} from 'vue-i18n'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import ButtonComponent from '@toolify/client/src/modules/ui/components/ButtonComponent.vue'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import {StyleVariantType} from '@toolify/client/src/modules/ui/enum/StyleVariantType'
import {ColorVariantType} from '@toolify/client/src/modules/ui/enum/ColorVariantType'
import {SizeVariantType} from '@toolify/client/src/modules/ui/enum/SizeVariantType'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {useDragSource} from '@toolify/client/src/modules/ui/composables/useDragSource'
import {IWorkspaceTab} from '@toolify/client/src/modules/workspaceTabs/types/IWorkspaceTab'
import {IWorkspaceTabDragPayload} from '@toolify/client/src/modules/workspaceTabs/types/IWorkspaceTabDragPayload'
import {useWorkspaceTabPresentation} from '@toolify/client/src/modules/workspaceTabs/composables/useWorkspaceTabPresentation'

interface Props {
  tab: IWorkspaceTab
}

const props = defineProps<Props>()
const emit = defineEmits<{
  activate: []
  close: []
}>()

const {t} = useI18n()
const {getTabTitle, getTabIconNameType, getTabIconColor, isTabActive} = useWorkspaceTabPresentation()
const title = computed(() => getTabTitle(props.tab))
const iconNameType = computed(() => getTabIconNameType(props.tab))
const iconColor = computed(() => getTabIconColor(props.tab))
const isActive = computed(() => isTabActive(props.tab))

const {sourceBindings, isDragging} = useDragSource<IWorkspaceTabDragPayload>({
  payload: () => ({
    kind: 'workspaceTab',
    tabKey: props.tab.key,
  }),
  preview: () => ({
    iconNameType: iconNameType.value,
    name: title.value,
  }),
})

function onClose(event: MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
  emit('close')
}
</script>

<template>
  <div
    class="workspace-tab"
    :class="{'is-active': isActive, 'is-dragging': isDragging}"
    :data-drop-item-id="tab.key"
    draggable="false"
    v-on="sourceBindings"
    @click="emit('activate')"
    @auxclick.middle.prevent="emit('close')"
  >
    <div class="tab-inner" data-drop-line-box>
      <IconComponent
        class="tab-icon"
        :name="iconNameType"
        :weightType="IconWeightType.Solid"
        :size="SizeVariantType.Small"
        :color="null"
        :style="iconColor ? {color: iconColor} : undefined"
      />
      <span class="tab-title">{{title}}</span>
      <ButtonComponent
        class="tab-close"
        :iconNameType="IconNameType.Xmark"
        :iconWeightType="IconWeightType.Solid"
        :style="StyleVariantType.ClearClear"
        :color="ColorVariantType.Dim"
        :size="SizeVariantType.Small"
        :aria-label="t('quickNavigation.workspaceTabs.close')"
        @click="onClose"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@toolify/client/src/styles/variables' as variables;

$tab-motion: 0.1s cubic-bezier(0, 0.5, 0, 1);

.workspace-tab {
  flex: 0 0 auto;
  width: 8rem;
  overflow: hidden;
  user-select: none;
  cursor: pointer;

  &.is-dragging {
    opacity: 0.5;
  }
}

.tab-inner {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: map-get(variables.$spacing, space-1-5);
  height: map-get(variables.$sizes, size-lg);
  padding: 0 map-get(variables.$spacing, space-1) 0 map-get(variables.$spacing, space-2);
  border-radius: variables.$borderRadius variables.$borderRadius 0 0;
  background: map-get(variables.$colors, container-lowest);
  color: map-get(variables.$colors, content-dim);
  position: relative;
  overflow: hidden;
  transition: padding $tab-motion, background $tab-motion, color $tab-motion;

  .workspace-tab:hover &,
  .workspace-tab.is-active & {
    padding-right: calc(#{map-get(variables.$componentSizes, small)} + 0.5rem);
  }

  .workspace-tab:hover & {
    background: map-get(variables.$colors, container-low);
    color: map-get(variables.$colors, content-normal);
  }

  .workspace-tab.is-active & {
    background: map-get(variables.$colors, surface);
    color: map-get(variables.$colors, content-bright);
    font-weight: 600;
  }
}

.tab-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tab-icon {
  flex-shrink: 0;
}

.tab-close {
  opacity: 0;
  position: absolute;
  right: 0.25rem;
  transform: translateX(100%);
  pointer-events: none;
  transition: opacity $tab-motion, transform $tab-motion;

  .workspace-tab:hover &,
  .workspace-tab.is-active & {
    opacity: 1;
    transform: translateX(0);
    pointer-events: auto;
  }
}
</style>
